import {
	SPACE_CALENDARS,
	SPACE_ANALYTICS,
	SPACE_SETTINGS_AUTOMATION,
	ADMIN_GLOBAL_AUTOMATION,
	ADMIN_CENTER,
	SPACE_BULK_TOOLS,
	CREATE_CONTENT,
} from '@confluence/named-routes';

export const PREMIUM_FEATURE_DEEPLINK_FALLBACKS = {
	AI: 'https://www.atlassian.com/platform/artificial-intelligence',
	TeamCalendars: 'https://www.atlassian.com/software/confluence/team-calendars',
	Whiteboard: 'https://www.atlassian.com/software/confluence/whiteboards',
	Automation: 'https://www.atlassian.com/software/confluence/automation',
	Analytics: 'https://support.atlassian.com/confluence-cloud/docs/view-insights-on-spaces/',
	CompanyHub: 'https://www.atlassian.com/software/confluence/use-cases/intranet-software',
	ContentManager: 'https://support.atlassian.com/confluence-cloud/docs/manage-your-content-tree/',
};

type PremiumFeatureDeeplinksProps = {
	recentSpaceKey?: string;
	starredSpaceKey?: string;
	currentSpaceKey?: string;
	personalSpaceKey?: string;
	popularSpaceKey?: string;
};

export class PremiumFeatureDeeplinks {
	private recentSpaceKey?: string;
	private starredSpaceKey?: string;
	private currentSpaceKey?: string;
	private personalSpaceKey?: string;
	private popularSpaceKey?: string;

	constructor({
		recentSpaceKey,
		starredSpaceKey,
		currentSpaceKey,
		personalSpaceKey,
		popularSpaceKey,
	}: PremiumFeatureDeeplinksProps = {}) {
		this.recentSpaceKey = recentSpaceKey;
		this.starredSpaceKey = starredSpaceKey;
		this.currentSpaceKey = currentSpaceKey;
		this.personalSpaceKey = personalSpaceKey;
		this.popularSpaceKey = popularSpaceKey;
	}

	public getAi() {
		return PREMIUM_FEATURE_DEEPLINK_FALLBACKS.AI;
	}

	public getTeamCalendars() {
		const spaceKeys = [this.currentSpaceKey, this.starredSpaceKey, this.recentSpaceKey];
		const spaceKey = spaceKeys.find((spaceKey) => spaceKey !== null && spaceKey !== undefined);
		if (!spaceKey) {
			return PREMIUM_FEATURE_DEEPLINK_FALLBACKS.TeamCalendars;
		}
		return SPACE_CALENDARS.toUrl({ spaceKey });
	}

	public getWhiteboard({ source }: { source?: string } = {}) {
		if (!this.personalSpaceKey || !source) {
			return PREMIUM_FEATURE_DEEPLINK_FALLBACKS.Whiteboard;
		}

		return CREATE_CONTENT.toUrl(
			{ contentType: 'whiteboard' },
			{ query: { spaceKey: this.personalSpaceKey, source } },
		);
	}

	public getAutomationEndUser() {
		if (!this.personalSpaceKey) {
			return PREMIUM_FEATURE_DEEPLINK_FALLBACKS.Automation;
		}

		return SPACE_SETTINGS_AUTOMATION.toUrl(
			{
				spaceKey: this.personalSpaceKey,
			},
			{ hash: '#/tab/rule-library' },
		);
	}

	public getAutomationAdmin() {
		return ADMIN_GLOBAL_AUTOMATION.toUrl({}, { hash: '#/tab/rule-library' });
	}

	public getAnalytics() {
		if (!this.popularSpaceKey) {
			return PREMIUM_FEATURE_DEEPLINK_FALLBACKS.Analytics;
		}
		return SPACE_ANALYTICS.toUrl({ spaceKey: this.popularSpaceKey });
	}

	public getCompanyHub({ isCompanyHubEnabled }: { isCompanyHubEnabled?: boolean } = {}) {
		if (!isCompanyHubEnabled) {
			return PREMIUM_FEATURE_DEEPLINK_FALLBACKS.CompanyHub;
		}

		/* /wiki/company-hub route shows not found screen if CH hasn't been created, so we are using the create CH aritcle link for now
		 Details: https://atlassian.slack.com/archives/C05G147TUQ1/p1725038871932349 */
		return 'https://support.atlassian.com/confluence-cloud/docs/create-and-customize-a-company-hub/';
	}

	public getMissionControl() {
		return ADMIN_CENTER.toUrl();
	}

	public getContentManager() {
		if (!this.popularSpaceKey) {
			return PREMIUM_FEATURE_DEEPLINK_FALLBACKS.ContentManager;
		}
		return SPACE_BULK_TOOLS.toUrl({ spaceKey: this.popularSpaceKey });
	}
}
